// netlify/functions/lib/project-chat-transcript.ts
//
// Bounding the conversation a chat turn re-sends to the model. Imported by BOTH sides: the client trims
// before the POST so the wire payload stays small, and the server re-applies the identical rules so the
// bound holds regardless of what a stale client sends.
//
// Sharing one function (rather than a count-based cap on the client and a char-based one on the server)
// is what makes the client trim provably lossless: what it drops is exactly what the server would have
// dropped anyway, so the model sees a byte-identical transcript either way.
//
// The trimmed-conversation NOTICE is deliberately NOT produced here — the server renders it from the
// client's counts plus its own (see combineTrimCounts). Keeping the notice out of the output is what
// makes capTranscript idempotent: running it on its own output is a fixed point, whereas feeding a
// notice back in would spend budget on it and eventually stack a second one.

/** Cap on the transcript re-sent on every turn. The whole history goes over the wire each time and was
 *  never bounded, so one user pasting a large document made every LATER turn slower too — the reason a
 *  project's chat, once it started dropping turns, kept dropping them while a fresh project answered the
 *  same paste in seconds. Trimmed oldest-first (the recent turns are what the reply depends on). */
export const MAX_TRANSCRIPT_CHARS = 60_000
/** No single message may occupy more than this much of that budget, so one huge paste can't evict the
 *  entire conversation around it. */
export const MAX_MESSAGE_CHARS = 24_000
/** How many system-generated notices (rows carrying a `source`, e.g. an exploration pausing for a
 *  login) may occupy the transcript budget.
 *
 *  These are posted by QA itself, not written by anyone in the conversation, and budgeted together with
 *  dialogue they win purely by volume. What forced this cap: qa.replay.io's chat was 2,670 notices to 37
 *  user messages (~127 notices a day), and one turn there sent 2,688 messages of which the 93 that
 *  survived were 91 notices, a trim marker, and the question being asked — every previous thing the user
 *  had said was gone.
 *
 *  The bulk of that volume was the run-scoped "a round started / finished" notices, and those no longer
 *  land here at all: they are stored against the round they describe (lib/test-run-notices.ts) and shown
 *  on its Test Run page. So this cap is no longer what holds the line — do not read the numbers above as
 *  current. It stays because the notices that DID remain (login pauses, obstacles, credit pauses) are
 *  still unbounded in principle, and they remain the least valuable thing in the transcript: the
 *  per-turn PROJECT CONTEXT block already tells the agent, live, what QA is running and what it found. */
export const MAX_TRANSCRIPT_NOTICES = 8

export type TranscriptRole = 'user' | 'assistant'

export interface TranscriptMessage {
  role: TranscriptRole
  content: string
  /** Set on system-generated messages (e.g. 'github-pr:ver-…'); absent on anything a human or the agent
   *  said in the conversation. Mirrors project_chat_messages.source. */
  source?: string | null
}

/** The CLIENT-generated chat failure notices — the "⚠️ …" one-liners ProjectChat writes into the
 *  transcript (via appendAssistant) when a turn was dropped, expired, timed out, or came back empty.
 *  They are the single source of truth: chatTelemetry.chatFailureNotice returns exactly these strings,
 *  so a message equal to one of them is unambiguously a UI artifact rather than something the agent said.
 *
 *  Defined HERE — not in the client-only chatTelemetry — because this shared module is the one both the
 *  client wire-trim and the server agent loop import, so it is the only place a failure notice can be
 *  RECOGNIZED on the way to the model (see capTranscript's pass 0 and isClientFailureNotice). */
export const CHAT_FAILURE_NOTICES = {
  expired: '⚠️ Your session has expired, so I could not answer that. Nothing was changed — please sign in again and re-send it.',
  truncated: "⚠️ That took too long and my reply was cut off before it reached you. Nothing was changed — ask me again (a shorter, more specific question is likely to come back faster).",
  empty: '⚠️ I finished that turn without producing a reply. Nothing was changed — please ask me again.',
  unreachable: "⚠️ I couldn't reach the QA assistant just now. Nothing was changed — please try again in a moment.",
  generic: '⚠️ Something went wrong answering that. Nothing was changed — please try again.',
} as const

const FAILURE_NOTICE_SET: ReadonlySet<string> = new Set<string>(Object.values(CHAT_FAILURE_NOTICES))

/** True for an assistant message that is one of the client failure notices above. These are written to
 *  the transcript so the STORED conversation shows a dropped turn instead of an unanswered question, but
 *  they carry nothing the model needs — and a project whose chat was repeatedly timing out accumulates a
 *  run of them, which, replayed to the model as its OWN prior replies, derails the next turn into
 *  answering none of what the user asked (an off-topic reply, or another apology). So they are kept out
 *  of what the model reads. Matched exactly against the known set rather than by an "⚠️" prefix, which a
 *  legitimate assistant reply could also start with. */
export function isClientFailureNotice(m: TranscriptMessage): boolean {
  return m.role === 'assistant' && !m.source && FAILURE_NOTICE_SET.has(m.content.trim())
}

/** How much was left out, split by kind so the notice can say which — "2,504 automated notices" and "91
 *  older messages" call for very different behaviour from the agent, and collapsing them into one number
 *  reads as though a long conversation was lost when almost none of it was. */
export interface TrimCounts {
  messages: number
  notices: number
}

export interface CappedTranscript {
  messages: TranscriptMessage[]
  trimmed: TrimCounts
  originalChars: number
  sentChars: number
}

function isNotice(m: TranscriptMessage): boolean {
  return typeof m.source === 'string' && m.source.length > 0
}

const elision = (omitted: number) => `\n\n[… ${omitted} characters omitted from the middle of this message …]\n\n`
// Room set aside for that marker inside the budget. The marker is 61 characters plus the digits of the
// omitted count, so this is never tight — it just has to be a CONSTANT, so the kept halves don't depend
// on a length that depends on them.
const ELISION_RESERVE = 120

/** Middle-truncate one over-long message — both ends of a pasted document carry meaning — marking the
 *  elision so the model knows it is not seeing the whole thing and can ask.
 *
 *  The result must itself be <= MAX_MESSAGE_CHARS, marker included, which is why the reserve is subtracted
 *  before halving. Producing exactly MAX_MESSAGE_CHARS of text PLUS a marker (as this did originally, when
 *  it only ever ran once, server-side) is silently wrong now that the client clamps first: the server's
 *  pass would truncate the truncation and nest a second marker inside the first. */
function clampMessage(m: TranscriptMessage): TranscriptMessage {
  if (m.content.length <= MAX_MESSAGE_CHARS) return m
  const half = Math.floor((MAX_MESSAGE_CHARS - ELISION_RESERVE) / 2)
  const omitted = m.content.length - half * 2
  return { ...m, content: `${m.content.slice(0, half)}${elision(omitted)}${m.content.slice(-half)}` }
}

/** Trim `messages` to the transcript budget, keeping the most recent turns. Over-long single messages are
 *  middle-truncated (both ends of a pasted document carry meaning) rather than dropped, and the elision
 *  is marked so the model knows it is not seeing the whole thing and can ask. */
export function capTranscript(messages: TranscriptMessage[]): CappedTranscript {
  // Pass 0 — drop the client failure notices outright, before anything is counted. They are UI
  // artifacts (see isClientFailureNotice), not conversation, so removing them is NOT a trim: they never
  // count toward originalChars or the trimmed banner, which would otherwise tell the agent a real
  // conversation was lost. Doing it first also keeps a run of them (a chat that kept timing out) from
  // reaching the model at all — replayed as the assistant's own prior replies, they derail the next turn.
  const conversation = messages.filter((m) => !isClientFailureNotice(m))
  const originalChars = conversation.reduce((n, m) => n + m.content.length, 0)

  // Pass 1 — thin the notices down to MAX_TRANSCRIPT_NOTICES, newest kept, BEFORE the char budget runs.
  // Doing it first is the point: otherwise a flood of notices exhausts the budget before the walk-back
  // ever reaches the dialogue underneath them.
  let noticeBudget = MAX_TRANSCRIPT_NOTICES
  const withinNoticeCap: TranscriptMessage[] = []
  let droppedNotices = 0
  for (let i = conversation.length - 1; i >= 0; i--) {
    const m = conversation[i]!
    if (isNotice(m)) {
      if (noticeBudget <= 0) { droppedNotices += 1; continue }
      noticeBudget -= 1
    }
    withinNoticeCap.unshift(m)
  }

  // Pass 2 — clamp any single over-long message.
  const clamped = withinNoticeCap.map(clampMessage)

  // Pass 3 — walk backwards from the newest turn, keeping messages until the budget runs out.
  const kept: TranscriptMessage[] = []
  let total = 0
  for (let i = clamped.length - 1; i >= 0; i--) {
    const m = clamped[i]!
    // Always keep the latest message even if it alone exceeds the budget — it IS the question.
    if (total + m.content.length > MAX_TRANSCRIPT_CHARS && kept.length > 0) break
    kept.unshift(m)
    total += m.content.length
  }

  return {
    messages: kept,
    trimmed: { messages: clamped.length - kept.length, notices: droppedNotices },
    originalChars,
    sentChars: total,
  }
}

/** Fold the client's reported trim into the server's own, so the notice reflects everything left out
 *  across both — not just the remainder the server happened to see. */
export function combineTrimCounts(client: Partial<TrimCounts> | undefined, server: TrimCounts): TrimCounts {
  return {
    messages: (client?.messages ?? 0) + server.messages,
    notices: (client?.notices ?? 0) + server.notices,
  }
}

/** The message prepended to a trimmed transcript, or null when nothing was left out. Phrased so the agent
 *  asks for what it is missing instead of guessing, and so a wall of omitted notices is not mistaken for
 *  a lost conversation. */
export function trimmedTranscriptNotice(trimmed: TrimCounts): string | null {
  const parts: string[] = []
  if (trimmed.messages > 0) parts.push(`${trimmed.messages} older message(s)`)
  if (trimmed.notices > 0) parts.push(`${trimmed.notices} automated QA status notice(s)`)
  if (parts.length === 0) return null
  return `[Earlier conversation trimmed: ${parts.join(' and ')} are not shown. Answer from the messages below and the project context; if you need something from earlier in the conversation, say so and ask the user to repeat it.]`
}
